import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import App from "../App";
import DevLoginPage from "../pages/DevLoginPage";
import OrgSignupConfirmationPage from "../pages/OrgSignupConfirmationPage";
import OrgSignupPage from "../pages/OrgSignupPage";
import TenantSelectionPage from "../pages/TenantSelectionPage";
import { useAuth } from "../contexts/AuthContext";
import { isLocalDevAppMode } from "../utils/appMode";
import { isSamiIamEnabled } from "../utils/samiIam";
import { isJwtExpired } from "../utils/jwtUtils";

const AppGate: React.FC = () => {
  const {
    token,
    loading,
    error,
    loginWithDevToken,
    pendingTenantSelection,
    pendingOrgSignup,
    orgSignupComplete,
  } = useAuth();
  const devMode = isLocalDevAppMode();
  const iamEnabled = isSamiIamEnabled();
  const hasValidSession = Boolean(token) && !isJwtExpired(token);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (pendingOrgSignup && orgSignupComplete) {
    return <OrgSignupConfirmationPage />;
  }

  if (pendingOrgSignup && !orgSignupComplete) {
    return <OrgSignupPage />;
  }

  if (pendingTenantSelection) {
    return <TenantSelectionPage />;
  }

  if (!hasValidSession) {
    if (devMode) {
      return <DevLoginPage onLogin={loginWithDevToken} error={error} />;
    }

    if (iamEnabled) {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            px: 2,
          }}
        >
          <Typography color="text.secondary">
            Redirecting to sign in…
          </Typography>
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          px: 2,
        }}
      >
        <Typography color="text.secondary">
          You are signed out. Sign in through your organization portal to continue.
        </Typography>
        <Box
          component="a"
          href="/"
          sx={{ color: "primary.main", fontWeight: 500, textDecoration: "none" }}
        >
          Go to home
        </Box>
      </Box>
    );
  }

  return <App />;
};

export default AppGate;
